import React, { useState, useMemo, useEffect } from "react";
import { X, ArrowLeft } from "lucide-react";
import { entityKindMap } from "../../utils";
import EntityFactInput from "./EntityFactInput";
import {
  EntityKind,
  Fact,
  ScalarFact,
  ArrayFact,
  SchemaFact,
  ScalarKind,
  FactValue,
  EntitySchema,
  schemaFactToDefinition,
} from "../../Dashboard/types";
import { isAuthorableFact, useEntitySchema } from "../../Data";
import { fetchEntity } from "../../Dashboard/api";

interface EntityFactInputLoaderProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  // True only when a genuine prior step exists (the type chooser) — see
  // EntityFactInput (#11426 component sweep, M1).
  showBack?: boolean;
  entityType: EntityKind;
  onSuccess?: () => void;
  initialName?: string;
  preventNavigation?: boolean;
  contextEntity?: EntitySchema; // Current entity context (e.g., manager when creating vehicle)
}

const EntityFactInputLoader: React.FC<EntityFactInputLoaderProps> = ({
  isOpen,
  onClose,
  onBack,
  showBack = false,
  entityType,
  onSuccess,
  initialName,
  preventNavigation,
  contextEntity,
}) => {
  const entityInfo = entityKindMap[entityType];
  const [hydratedFacts, setHydratedFacts] = useState<Fact[] | null>(null);
  const [isHydrating, setIsHydrating] = useState(false);

  // Use Tanstack Query to fetch schema data
  const {
    data: schemaData,
    isLoading,
    error,
    isError,
  } = useEntitySchema(entityType);

  // Transform schema facts into the format expected by EditableFactRenderer
  const { schemaFacts, schemaDefinitions } = useMemo(() => {
    if (!schemaData) {
      return { schemaFacts: [], schemaDefinitions: [] };
    }

    // System-written facts (Date Created, Created By, Last Updated At/By,
    // the Last Interaction family — provenance "derived"/"readonly") are
    // not authorable: a create form inviting you to set Last Updated By is
    // asking for false data (#11426 component sweep, M1). The same test
    // decides whether the kind is offered for creation at all (#12224).
    const authorableFacts = schemaData.facts.filter(isAuthorableFact);

    // Check if we should pre-fill Manager fact when creating a vehicle from a manager page
    const shouldPreFillManager =
      entityType === "vehicle" &&
      contextEntity?.kind === "manager" &&
      contextEntity?.id &&
      contextEntity?.name;

    // Find the manager fact in the schema by its DESIGNATION (#11270) — never
    // by name, since orgs name their designated fund-manager attribute
    // anything. It must still accept manager entities.
    const managerFactSchema = shouldPreFillManager
      ? schemaData.facts.find(
          (fact: SchemaFact) =>
            fact.designates === "fund_manager" &&
            fact.entity_types?.includes("manager")
        )
      : null;

    // Check if we should pre-fill the employer when creating a person from a
    // company or manager page
    const shouldPreFillRole =
      entityType === "person" &&
      (contextEntity?.kind === "company" ||
        contextEntity?.kind === "manager") &&
      contextEntity?.id &&
      contextEntity?.name;

    // Find the field the employer link belongs in by its DESIGNATION (#11270)
    // — never by name, since orgs name their fields anything. A person's role
    // may be split across two fields (#11392), so the employer field is
    // preferred and the role field is the fallback for orgs holding both
    // halves on one field. Either way it must accept the context record's kind.
    const acceptsContextKind = (fact: SchemaFact) =>
      !!fact.entity_types &&
      fact.entity_types.length > 0 &&
      fact.entity_types.includes(contextEntity!.kind);
    const roleFactSchema = shouldPreFillRole
      ? (schemaData.facts.find(
          (fact: SchemaFact) =>
            fact.designates === "person_employer" && acceptsContextKind(fact)
        ) ??
        schemaData.facts.find(
          (fact: SchemaFact) =>
            fact.designates === "person_role" && acceptsContextKind(fact)
        ))
      : null;

    const transformedFacts: Fact[] = authorableFacts.map(
      (schemaFact: SchemaFact) => {
        const baseFact = schemaFactToDefinition(schemaFact);

        // Check if this is the designated fund-manager fact to pre-fill
        const isManagerFact =
          managerFactSchema &&
          schemaFact.designates === "fund_manager" &&
          schemaFact.id === managerFactSchema.id;

        // Check if this is the field the employer link should be pre-filled into
        const isRoleFact =
          roleFactSchema && schemaFact.id === roleFactSchema.id;

        if (schemaFact.is_array) {
          // Create an ArrayFact with default values if available
          const defaultArray: FactValue[] = [];

          // Add scalar defaults if present
          if (schemaFact.default_scalar_value) {
            // Scalar default should be an array for array fields
            const scalarValues = Array.isArray(schemaFact.default_scalar_value)
              ? schemaFact.default_scalar_value
              : [schemaFact.default_scalar_value];
            scalarValues.forEach((val) => {
              defaultArray.push({ scalar: val, entity: null });
            });
          }

          // Add entity defaults if present
          if (
            schemaFact.default_entity_ids &&
            schemaFact.default_entity_ids.length > 0
          ) {
            const entityKind = schemaFact.entity_types?.[0] || "company";
            schemaFact.default_entity_ids.forEach((entityId) => {
              defaultArray.push({
                scalar: null,
                entity: {
                  id: entityId,
                  kind: entityKind,
                  name: "",
                },
              });
            });
          }

          // Pre-fill Manager fact if applicable
          if (isManagerFact && contextEntity) {
            defaultArray.push({
              scalar: null,
              entity: {
                id: contextEntity.id,
                kind: contextEntity.kind,
                name: contextEntity.name,
              },
            });
          }

          // Pre-fill Role fact if applicable
          if (isRoleFact && contextEntity) {
            defaultArray.push({
              scalar: null,
              entity: {
                id: contextEntity.id,
                kind: contextEntity.kind,
                name: contextEntity.name,
              },
            });
          }

          return {
            ...baseFact,
            array: defaultArray,
          } as ArrayFact;
        } else {
          // Create a ScalarFact with default values if available
          // For hierarchical fields, scalar should be an array
          if (schemaFact.is_hierarchical && schemaFact.default_scalar_value) {
            const scalarArray = Array.isArray(schemaFact.default_scalar_value)
              ? schemaFact.default_scalar_value
              : [schemaFact.default_scalar_value];
            return {
              ...baseFact,
              scalar: scalarArray,
              entity: null,
            } as ScalarFact;
          }

          // For non-hierarchical fields
          const hasEntityDefault =
            schemaFact.default_entity_ids &&
            schemaFact.default_entity_ids.length > 0;

          // Pre-fill Manager fact if applicable (for non-array facts)
          if (isManagerFact && contextEntity) {
            return {
              ...baseFact,
              scalar: schemaFact.default_scalar_value ?? null,
              entity: {
                id: contextEntity.id,
                kind: contextEntity.kind,
                name: contextEntity.name,
              },
            } as ScalarFact;
          }

          // Pre-fill Role fact if applicable (for non-array facts)
          if (isRoleFact && contextEntity) {
            return {
              ...baseFact,
              scalar: schemaFact.default_scalar_value ?? null,
              entity: {
                id: contextEntity.id,
                kind: contextEntity.kind,
                name: contextEntity.name,
              },
            } as ScalarFact;
          }

          return {
            ...baseFact,
            scalar: schemaFact.default_scalar_value ?? null,
            entity: hasEntityDefault
              ? {
                  id: schemaFact.default_entity_ids![0],
                  kind: schemaFact.entity_types?.[0] || "company",
                  name: "",
                }
              : null,
          } as ScalarFact;
        }
      }
    );

    return {
      schemaFacts: transformedFacts,
      schemaDefinitions: authorableFacts,
    };
  }, [schemaData, entityType, contextEntity]);

  // Hydrate entity names for default entities
  useEffect(() => {
    if (!schemaFacts || schemaFacts.length === 0) {
      setHydratedFacts(null);
      return;
    }

    // Collect all entity IDs that need to be fetched
    const entitiesToFetch: Array<{ entityId: string; entityKind: string }> = [];

    schemaFacts.forEach((fact) => {
      if ("array" in fact && fact.array) {
        fact.array.forEach((item) => {
          if (item.entity && item.entity.name === "") {
            entitiesToFetch.push({
              entityId: item.entity.id,
              entityKind: item.entity.kind,
            });
          }
        });
      } else if ("entity" in fact && fact.entity && fact.entity.name === "") {
        entitiesToFetch.push({
          entityId: fact.entity.id,
          entityKind: fact.entity.kind,
        });
      }
    });

    // If no entities need hydration, use the facts as-is
    if (entitiesToFetch.length === 0) {
      setHydratedFacts(schemaFacts);
      return;
    }

    // Fetch all entities in parallel
    setIsHydrating(true);
    Promise.all(
      entitiesToFetch.map(({ entityId, entityKind }) =>
        fetchEntity(entityKind, entityId)
          .then((entity) => ({ entityId, entityKind, name: entity.name }))
          .catch((err) => {
            console.error(`Failed to fetch entity ${entityId}:`, err);
            return { entityId, entityKind, name: `Entity ${entityId}` };
          })
      )
    ).then((fetchedEntities) => {
      // Create a lookup map
      const entityMap = new Map(
        fetchedEntities.map((e) => [`${e.entityKind}:${e.entityId}`, e.name])
      );

      // Update the facts with the fetched names
      const updatedFacts = schemaFacts.map((fact) => {
        if ("array" in fact && fact.array) {
          return {
            ...fact,
            array: fact.array.map((item) => {
              if (item.entity && item.entity.name === "") {
                const key = `${item.entity.kind}:${item.entity.id}`;
                const name = entityMap.get(key) || item.entity.name;
                return {
                  ...item,
                  entity: { ...item.entity, name },
                };
              }
              return item;
            }),
          };
        } else if ("entity" in fact && fact.entity && fact.entity.name === "") {
          const key = `${fact.entity.kind}:${fact.entity.id}`;
          const name = entityMap.get(key) || fact.entity.name;
          return {
            ...fact,
            entity: { ...fact.entity, name },
          };
        }
        return fact;
      });

      setHydratedFacts(updatedFacts);
      setIsHydrating(false);
    });
  }, [schemaFacts]);

  if (!isOpen) return null;

  // Loading state (including hydration)
  if (isLoading || isHydrating) {
    return (
      <div
        // A dialog by role, so the Facts grid's close-on-Escape can tell keys
        // typed in here from keys typed in the row that opened it (#11880).
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl relative m-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center border-b px-6 py-4">
            <div className="flex items-center gap-4">
              {showBack && (
                <button
                  onClick={onBack}
                  aria-label="Back to record types"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ArrowLeft size={24} />
                </button>
              )}
              <h2 className="text-2xl font-semibold text-gray-900">
                Create New {entityInfo?.label || entityType}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading schema...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div
        // A dialog by role, so the Facts grid's close-on-Escape can tell keys
        // typed in here from keys typed in the row that opened it (#11880).
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl relative m-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center border-b px-6 py-4">
            <div className="flex items-center gap-4">
              {showBack && (
                <button
                  onClick={onBack}
                  aria-label="Back to record types"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ArrowLeft size={24} />
                </button>
              )}
              <h2 className="text-2xl font-semibold text-gray-900">
                Create New {entityInfo?.label || entityType}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-500 mb-4">
                  {error instanceof Error
                    ? error.message
                    : "Failed to load schema"}
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state - render the inner component
  if (hydratedFacts && hydratedFacts.length > 0) {
    return (
      <div
        // A dialog by role, so the Facts grid's close-on-Escape can tell keys
        // typed in here from keys typed in the row that opened it (#11880).
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-6xl relative m-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <EntityFactInput
            onClose={onClose}
            onBack={onBack}
            showBack={showBack}
            entityType={entityType}
            schemaFacts={hydratedFacts}
            schemaDefinitions={schemaDefinitions}
            onSuccess={onSuccess}
            initialName={initialName}
            preventNavigation={preventNavigation}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default EntityFactInputLoader;
